import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Database, Info, RefreshCw, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  useReindexAllSearchIndexMutation,
  useSearchIndexHealthQuery,
  type SearchIndexHealth,
  type SearchIndexHealthStatus,
  type StaleDocument,
} from '@/lib/api/search-index-health';
import { cn } from '@/lib/utils/cn';

/**
 * T-0116 / E02-F03 — Search-index health modal (`data-open-index`).
 *
 * T-0120 (PR #142) shipped the per-engagement health composite +
 * engagement-wide reindex-all endpoint; this modal is now wired
 * end-to-end against both:
 *
 *   - `GET .../search-index/health` populates the per-engagement chunk
 *     + doc counts, the engagement-wide `last_indexed_at`, the stale-
 *     documents list, and the derived `health_status` (fresh / stale /
 *     indexing) the namespace badge surfaces.
 *   - `POST .../search-index/reindex-all` powers the "Reindex engagement"
 *     CTA. The mutation honors `Idempotency-Key` so a click-then-network-
 *     blip doesn't fan-out twice; the indexing worker upserts by
 *     `search_doc_id` so replays don't duplicate chunks.
 *
 * Visual contract: README §4 modal table + the documents screen's
 * "Search index" button. The namespace badge uses posture-tinted chips
 * for the three health states. Stale documents render as a separate
 * card listing the document title + delta between last-indexed and
 * last-modified.
 *
 * Auth: engagement-member floor on the GET; engagement_lead+ on the
 * POST (the backend gates this — consultants get 403). The modal lets
 * the server surface the gate as an inline error rather than re-
 * checking the role client-side.
 */

export interface SearchIndexModalProps {
  engagementId: string;
  engagementName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const HEALTH_STATUS_TONE: Record<SearchIndexHealthStatus, string> = {
  fresh: 'bg-posture-healthy-tint text-posture-healthy',
  stale: 'bg-posture-at-risk-tint text-posture-at-risk',
  indexing: 'bg-ai-surface text-ai-ink',
};

const HEALTH_STATUS_LABEL: Record<SearchIndexHealthStatus, string> = {
  fresh: 'Fresh',
  stale: 'Stale',
  indexing: 'Indexing',
};

export function SearchIndexModal({
  engagementId,
  engagementName,
  open,
  onOpenChange,
}: SearchIndexModalProps) {
  const query = useSearchIndexHealthQuery(open ? engagementId : undefined);
  const reindex = useReindexAllSearchIndexMutation(engagementId);
  const data = query.data ?? null;

  const handleReindexAll = React.useCallback(() => {
    if (!engagementId || reindex.isPending) return;
    const idempotencyKey =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `reindex-all-${engagementId}-${Date.now()}`;
    reindex.mutate({ idempotencyKey });
  }, [engagementId, reindex]);

  const titleId = 'search-index-title';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(820px,94vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <Header
            titleId={titleId}
            engagementId={engagementId}
            engagementName={engagementName}
            healthStatus={data?.health_status ?? null}
            onReindexAll={handleReindexAll}
            reindexing={reindex.isPending}
            reindexResult={reindex.data ?? null}
            disableReindex={query.isLoading || (data?.document_count ?? 0) === 0}
          />
          <div className="overflow-y-auto bg-paper-50 px-6 py-5">
            {query.isLoading ? (
              <div className="flex items-center gap-2 text-meta text-ink-500">
                <Spinner /> Loading index health…
              </div>
            ) : query.error ? (
              <ErrorMessage error={query.error} />
            ) : data ? (
              <>
                <SummaryTiles data={data} />
                {reindex.error ? (
                  <div className="mt-4">
                    <ErrorMessage error={reindex.error} />
                  </div>
                ) : null}
                <h3 className="mt-5 text-h3 font-semibold text-ink-900">Stale documents</h3>
                <p className="mt-1 text-meta text-ink-500">
                  A document is stale when the latest indexed chunk is older than the latest
                  uploaded version. The reindex-all CTA re-runs embedding + upsert against the
                  per-engagement namespace.
                </p>
                <StaleDocumentList items={data.stale_documents} />
              </>
            ) : null}
          </div>
          <Footer />
          <Dialog.Close
            aria-label="Close search index inspector"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Header({
  titleId,
  engagementId,
  engagementName,
  healthStatus,
  onReindexAll,
  reindexing,
  reindexResult,
  disableReindex,
}: {
  titleId: string;
  engagementId: string;
  engagementName: string | undefined;
  healthStatus: SearchIndexHealthStatus | null;
  onReindexAll: () => void;
  reindexing: boolean;
  reindexResult: { documents_enqueued: number } | null;
  disableReindex: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-6 py-4 pr-14">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-meta uppercase tracking-wide text-ink-500">
          <Database className="h-3 w-3" aria-hidden="true" />
          Search index health
        </p>
        <Dialog.Title id={titleId} className="mt-1 truncate text-h2 font-semibold text-ink-900">
          {engagementName ?? 'Engagement search index'}
        </Dialog.Title>
        <p className="mt-1 text-meta text-ink-500">
          This engagement&apos;s own search index
          {healthStatus ? (
            <>
              {' '}
              &middot;{' '}
              <span
                className={cn(
                  'inline-flex items-center rounded-chip px-1.5 py-0.5 text-[10px] font-semibold',
                  HEALTH_STATUS_TONE[healthStatus],
                )}
              >
                {HEALTH_STATUS_LABEL[healthStatus]}
              </span>
            </>
          ) : null}
        </p>
        {reindexResult ? (
          <p className="mt-1 text-meta text-posture-healthy">
            Re-indexed {reindexResult.documents_enqueued} document
            {reindexResult.documents_enqueued === 1 ? '' : 's'}.
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReindexAll}
          disabled={disableReindex || reindexing}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2.5',
            'text-meta font-medium text-ink-700 hover:bg-paper-100',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {reindexing ? <Spinner /> : <RefreshCw className="h-3 w-3" aria-hidden="true" />}
          {reindexing ? 'Reindexing…' : 'Reindex engagement'}
        </button>
      </div>
    </div>
  );
}

function SummaryTiles({ data }: { data: SearchIndexHealth }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {/* document_count is the engagement's live documents, indexed or not
          (deleted ones are left out); the Documents header says how many
          of them are indexed. */}
      <Tile
        label="Documents"
        value={String(data.document_count)}
        sub="in the engagement, not deleted"
      />
      <Tile
        label="Chunk count"
        value={String(data.chunk_count)}
        sub="indexed passages of those documents"
      />
      <Tile
        label="Last indexed"
        value={data.last_indexed_at ? data.last_indexed_at.slice(0, 10) : '—'}
        sub={data.last_indexed_at ? data.last_indexed_at.slice(11, 16) + ' UTC' : 'never indexed'}
      />
      <Tile
        label="Stale documents"
        value={String(data.stale_documents.length)}
        sub={data.stale_documents.length === 0 ? 'all up-to-date' : 're-upload after index'}
      />
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-card border border-ink-100 bg-paper-0 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</p>
      <p className="mt-1 text-h2 font-semibold tabular-nums text-ink-900">{value}</p>
      <p className="mt-0.5 text-meta text-ink-500">{sub}</p>
    </div>
  );
}

function StaleDocumentList({ items }: { items: StaleDocument[] }) {
  if (items.length === 0) {
    return (
      <div className="mt-3 rounded-card border border-ink-100 bg-paper-0 px-4 py-6 text-meta text-ink-500">
        No stale documents detected. Every indexed chunk is up-to-date with the latest uploaded
        version.
      </div>
    );
  }
  return (
    <ul className="mt-3 overflow-hidden rounded-card border border-ink-100">
      {items.map((d) => (
        <li
          key={d.document_id}
          className="flex flex-wrap items-baseline justify-between gap-3 border-t border-ink-100 bg-paper-0 px-4 py-2.5 first:border-t-0"
        >
          <div className="min-w-0">
            <div className="truncate text-body-sm font-medium text-ink-900" title={d.title}>
              {d.title}
            </div>
            <div
              className="mt-0.5 truncate font-mono text-[10px] text-ink-500"
              title={d.document_id}
            >
              {d.document_id}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-meta">
            <span className="text-ink-500">Indexed</span>
            <span className="text-ink-700">{d.last_indexed_at ?? '—'}</span>
            <span className="text-ink-500">Modified</span>
            <span className="text-posture-at-risk">{d.last_modified_at ?? '—'}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Footer() {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
      <p className="flex items-center gap-2 text-meta text-ink-500">
        <Info className="h-3 w-3" aria-hidden="true" />
        Reindex all re-reads every document that has been parsed. Running it again never duplicates
        passages.
      </p>
      <Dialog.Close
        className={cn(
          'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
          'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
        )}
      >
        Close
      </Dialog.Close>
    </div>
  );
}
